import React from "react";
import { Paragraph } from "../elements/Paragraph/Paragraph";
import { Image } from "../elements/Image/Image";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";

export const ShopifyMigration = ({ settings }) => {

  // Extract settings from props
  const {
    title = "How MindArc Can Support Your Migration",
    image = "",
    image_mobile = "",
    blocks = [],
    padding_top_bottom_desktop = 80,
    padding_left_right_desktop = 20,
    padding_top_botton_mobile = 40,
    padding_left_right_mobile = 12,
    desktop_min_height = 0,
    mobile_min_height = 0,
  } = settings.settings.section || {};

  const isMobile = useMobileBreakpoint(settings.settings.globals.mobile_breakpoint);

  // Get services from blocks
  const migrationServices = settings.settings.blocks
    .filter(block => block.type === "service")
    .map((block, index) => {
      const { settings: blockSettings } = block;
      
      return {
        id: `service-${index}`,
        iconType: blockSettings.icon,
        iconImage: blockSettings.icon_image,
        title: blockSettings.title,
        description: blockSettings.description
      };
    });

  const migrationImage = !isMobile ? image : image_mobile ? image_mobile : image;

  // Icon rendering function with image support
  const renderServiceIcon = (service) => {
    // If custom image is provided, use it
    if (service.iconImage) {
      return (
        <div className="w-8 h-8 md:w-10 md:h-10">
          <Image 
            imageUrl={service.iconImage}
            alt={`${service.title} icon`}
            classes="w-full h-full object-contain"
            height="40"
            width="40"
          />
        </div>
      );
    }
    
    // Otherwise, use SVG fallback
    return renderIcon(service.iconType);
  };

  // Icon mapping function (fallback SVG icons)
  const renderIcon = (iconType) => {
    switch(iconType) {
      case 'strategy':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path fillRule="evenodd" d="M7.502 6h7.128A3.375 3.375 0 0118 9.375v9.375a3 3 0 003-3V6.108c0-1.505-1.125-2.811-2.664-2.94a48.972 48.972 0 00-.673-.05A3 3 0 0015 1.5h-1.5a3 3 0 00-2.663 1.618c-.225.015-.45.032-.673.05C8.662 3.295 7.554 4.542 7.502 6zM13.5 3A1.5 1.5 0 0012 4.5h4.5A1.5 1.5 0 0015 3h-1.5z" clipRule="evenodd" />
            <path fillRule="evenodd" d="M3 9.375C3 8.339 3.84 7.5 4.875 7.5h9.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 013 20.625V9.375zM6 12a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H6.75a.75.75 0 01-.75-.75V12zm2.25 0a.75.75 0 01.75-.75h3.75a.75.75 0 010 1.5H9a.75.75 0 01-.75-.75zM6 15a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H6.75a.75.75 0 01-.75-.75V15zm2.25 0a.75.75 0 01.75-.75h3.75a.75.75 0 010 1.5H9a.75.75 0 01-.75-.75zM6 18a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H6.75a.75.75 0 01-.75-.75V18zm2.25 0a.75.75 0 01.75-.75h3.75a.75.75 0 010 1.5H9a.75.75 0 01-.75-.75z" clipRule="evenodd" />
          </svg>
        );
      case 'data':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path d="M3.375 3C2.339 3 1.5 3.84 1.5 4.875v.75c0 1.036.84 1.875 1.875 1.875h17.25c1.035 0 1.875-.84 1.875-1.875v-.75C22.5 3.839 21.66 3 20.625 3H3.375z" />
            <path fillRule="evenodd" d="M3.087 9l.54 9.176A3 3 0 006.62 21h10.757a3 3 0 002.995-2.824L20.913 9H3.087zm6.133 2.845a.75.75 0 011.06 0l1.72 1.72 1.72-1.72a.75.75 0 111.06 1.06l-1.72 1.72 1.72 1.72a.75.75 0 11-1.06 1.06L12 15.685l-1.72 1.72a.75.75 0 11-1.06-1.06l1.72-1.72-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
          </svg>
        );
      case 'development':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path fillRule="evenodd" d="M2.25 6a3 3 0 013-3h13.5a3 3 0 013 3v12a3 3 0 01-3 3H5.25a3 3 0 01-3-3V6zm3.97.97a.75.75 0 011.06 0l2.25 2.25a.75.75 0 010 1.06l-2.25 2.25a.75.75 0 01-1.06-1.06l1.72-1.72-1.72-1.72a.75.75 0 010-1.06zm4.28 4.28a.75.75 0 000 1.5h3a.75.75 0 000-1.5h-3z" clipRule="evenodd" />
          </svg>
        );
      case 'design':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path fillRule="evenodd" d="M20.599 1.5c-.376 0-.743.111-1.055.32l-5.08 3.385a18.747 18.747 0 00-3.471 2.987 10.04 10.04 0 014.815 4.815 18.748 18.748 0 002.987-3.472l3.386-5.079A1.902 1.902 0 0020.599 1.5zm-8.3 14.025a18.76 18.76 0 001.896-1.207 8.026 8.026 0 00-4.513-4.513A18.75 18.75 0 008.475 11.7l-.278.5a5.26 5.26 0 013.601 3.602l.502-.278zM6.75 13.5A3.75 3.75 0 003 17.25a1.5 1.5 0 01-1.601 1.497.75.75 0 00-.7 1.123 5.25 5.25 0 009.8-2.62 3.75 3.75 0 00-3.75-3.75z" clipRule="evenodd" />
          </svg>
        );
      case 'support':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path fillRule="evenodd" d="M15 3.75a.75.75 0 01.75-.75h4.5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0V5.56l-4.72 4.72a.75.75 0 11-1.06-1.06l4.72-4.72h-2.69a.75.75 0 01-.75-.75z" clipRule="evenodd" />
            <path fillRule="evenodd" d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 01-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 01-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 01-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584zM12 18a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div 
      className="w-full shopify-migration-section"
      style={{
        minHeight: isMobile ? undefined : `${desktop_min_height}px`,
        paddingTop: isMobile ? undefined : `${padding_top_bottom_desktop}px`,
        paddingBottom: isMobile ? undefined : `${padding_top_bottom_desktop}px`,
        paddingLeft: isMobile ? undefined : `${padding_left_right_desktop}px`,
        paddingRight: isMobile ? undefined : `${padding_left_right_desktop}px`
      }}
    >
      <div className="relative">
        {/* Title Section */}
        <div className="mb-12">
          <h2 className="h2 w-full lg:w-[634px] lg:pl-[134px]">
            {title}
          </h2>
        </div>
        
        {/* Main Content with Curved Image */}
        <div className="flex flex-col lg:flex-row relative">
          {/* Image Section with Curved Border */}
          <div className="w-full lg:w-[600px] mb-8 lg:mb-0 relative">
            <div className="relative h-full">
              <div className="overflow-hidden relative h-full" 
                style={{ 
                  borderTopRightRadius: isMobile ? '0' : '680px',
                  minHeight: isMobile ? '300px' : '400px'
                }}
              >
                <Image 
                  imageUrl={migrationImage}
                  alt="MindArc Shopify Migration Team" 
                  classes="w-full h-full object-cover"
                  height="100%"
                  width="100%"
                />
              </div>
            </div>
          </div>
          
          {/* Services List with Arc Shape */}
          <div className="w-full lg:w-1/2 relative">
            <div className="lg:w-full lg:h-full">
              {/* Services mapped in arc format */}
              <div className="flex flex-col justify-between h-full lg:py-4">
                {migrationServices.map((service, index) => (
                  <div 
                    key={service.id} 
                    className={`flex `}
                    style={{
                      transform: isMobile
                        ? 'none' // No transform on mobile
                        : index === 0
                          ? 'translateX(-330px)'
                          : index === 1
                          ? 'translateX(-140px)'
                          : index === 2
                          ? 'translateX(-35px)'
                          : index === 3
                          ? 'translateX(10px)'
                          : index === 4
                          ? 'translateX(30px)'
                          : undefined
                    }}
                  >
                    <div className="text-2xl md:text-3xl mr-4 text-primary flex-shrink-0">
                      {renderServiceIcon(service)}
                    </div>
                    <div>
                      <h4 className="h4">{service.title}</h4>
                      <Paragraph 
                        text={service.description} 
                        type="b_small_light mb-[60px]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-specific styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media (max-width: 768px) {
            .shopify-migration-section {
              padding-top: ${padding_top_botton_mobile}px;
              padding-bottom: ${padding_top_botton_mobile}px;
              padding-left: ${padding_left_right_mobile}px;
              padding-right: ${padding_left_right_mobile}px;
              min-height: ${mobile_min_height}px;
            }
          }
        `
      }} />
    </div>
  );
};

export default ShopifyMigration;